/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: utils.tsx
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 13/10/2025 - 19:09
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import axios from "axios";
import { toast } from "react-toastify";
import * as React from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export enum MenuAction {
   None = "",
   Dashboard = "Dashboard",
   OpAgrume = "OpAgrume",
   MigrSilaeOp = "MigrSilaeOp",
   Robopenpaye = "Robopenpaye",
}

export enum AccountAction {
   SetPasswd = "SetPasswd",
   AdminPage = "AdminPage",
   Logout = "Logout",
}

export enum MigrationAction {
   SearchEmp = "SearchEmp",
   ImportCsv = "ImportCsv",
   Migrate = "Migrate",
}

/**
 * Get the display string for a given action
 *
 * @param action The action for which to get the display string
 * @returns the display string
 */
export function actionDisplayString(action) {
   if (action === MenuAction.Dashboard) return "Tableau de Bord";
   else if (action === MenuAction.OpAgrume) return "Synchro Openpaye/Agrume";
   else if (action === MenuAction.MigrSilaeOp) return "Outils Silae/Openpaye";
   else if (action === MenuAction.Robopenpaye) return "Robot paye et DSN";
   else if (action === AccountAction.SetPasswd) return "Changer Mot de Passe";
   else if (action === AccountAction.AdminPage) return "Administration";
   else if (action === AccountAction.Logout) return "Déconnexion";
   else if (action === MigrationAction.SearchEmp) return "Recherche Salariés";
   else if (action === MigrationAction.ImportCsv) return "Importer CSV";
   else if (action === MigrationAction.Migrate) return "Migration";
   else return "Unknown";
}

/**
 * Colorize a stdout string in html format
 *
 * @param {string} str the string to colorize
 *
 * @returns the colorized string, containing the html tags
 */
export function formatString(str) {
   str = str.replaceAll("\\[90m", "<span class='text_black'>");
   str = str.replaceAll("\\[91m", "<span class='text_red'>");
   str = str.replaceAll("\\[92m", "<span class='text_green'>");
   str = str.replaceAll("\\[93m", "<span class='text_orange'>");
   str = str.replaceAll("\\[94m", "<span class='text_blue'>");
   str = str.replaceAll("\\[95m", "<span class='text_violet'>");
   str = str.replaceAll("\\[96m", "<span class='text_cyan'>");
   str = str.replaceAll("\\[97m", "<span class='text_white'>");
   str = str.replaceAll("\\[1m", "");

   str = str.replaceAll("\\[0m", "</span>");
   return str;
}

/**
 * Generate a password field
 *
 * @param label The name of the field, showed to the user
 * @param elementId The element id, so that document.getElementById can be used
 * @param showPassword Boolean state indicating if the password must be shown
 * @param onSetShowPassword SetState function to write the boolean's value
 * @returns
 */
export function passwordField(label, elementId, showPassword, onSetShowPassword) {
   const handleClickShowPassword = () => setShowPassword((show) => !show);

   const setShowPassword = React.useCallback(
      (event) => {
         onSetShowPassword(event);
      },
      [onSetShowPassword]
   );

   return (
      <FormControl className="w100" variant="outlined">
         <InputLabel htmlFor="outlined-adornment-password">{label}</InputLabel>
         <OutlinedInput
            id={elementId}
            type={showPassword ? "text" : "password"}
            endAdornment={
               <InputAdornment position="end">
                  <IconButton
                     aria-label={showPassword ? "hide the password" : "display the password"}
                     onClick={handleClickShowPassword}
                     edge="end"
                  >
                     {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
               </InputAdornment>
            }
            label={label}
         />
      </FormControl>
   );
}

/**
 * Axios custom interface for the dialog with the backend
 * Use for every API call to the backend
 */
export const apiClient = axios.create({
   baseURL: process.env.REACT_APP_BACKEND_URL,
   timeout: 1000,
   headers: { "Content-Type": "application/json" },
});

/**
 * Interceptor for the request
 * Adds the token to the request so the code can forget about it
 */
apiClient.interceptors.request.use(
   (config) => {
      // When every method but login, add the token
      if (config.url != "/login") {
         const token = localStorage.getItem("token");
         if (token) {
            // When GET, the data is passed through params, but it must be initialized
            // so that the token can be added to it
            if (config.method?.toLowerCase() == "get") {
               if (!config.params) {
                  config.params = { token: token };
               } else {
                  config.params["token"] = token;
               }
            } else {
               if (!config.data) {
                  config.data = { token: token };
               } else {
                  config.data["token"] = token;
               }
            }
         } else {
            toast.error("Missing token");
         }
      }
      // Override timeout for these requests as they're known to take a long time
      if (config.url == "/redirOpagrum") {
         config.timeout = 10000;
      }
      if (config.url == "/e2rhApi/getFolderInfo") {
         config.timeout = 20000;
      }
      return config;
   },
   (error) => Promise.reject(error)
);

/**
 * Interceptor for the response
 * Handle the error display, raises an Error if not HTTP code 200
 */
apiClient.interceptors.response.use(undefined, async (response) => {
   if (response.status != 200) {
      let headers = "";
      try {
         headers = response.response.headers.statustext;
      } catch {
         headers = "No description for the error";
      }
      const errMsg = `HTTP error: ${response.status}, message: ${headers}`;
      toast.error(errMsg);
      // throw new Error(errMsg);
   }
});
